import { useEffect, useState } from "react";
import { MousePointerClick, TrendingUp, IndianRupee, Percent, Store, RefreshCw, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatCard, SectionCard } from "@/components/ui-helpers";

const STATUS_TINT = {
  paid: "bg-emerald-100 text-emerald-700",
  payable: "bg-teal-100 text-teal-700",
  validated: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  rejected: "bg-rose-100 text-rose-700",
  unknown: "bg-slate-100 text-slate-600",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function AdminAffiliate() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/affiliate/admin/dashboard")
      .then((r) => setD(r.data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-affiliate">
      <PageHeader
        title="Affiliate Earnings"
        subtitle="Clicks, conversions and commission from Cuelinks & Travelpayouts partner links."
        icon={TrendingUp}
        actions={
          <button onClick={load} data-testid="affiliate-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 hover:border-slate-300">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500" data-testid="affiliate-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : !d ? (
        <p className="text-sm text-slate-500">Couldn't load affiliate stats.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard icon={MousePointerClick} label="Partner clicks" value={d.total_clicks} tint="blue" testid="stat-clicks" />
            <StatCard icon={TrendingUp} label="Conversions" value={d.total_conversions} tint="violet" testid="stat-conversions" />
            <StatCard icon={Percent} label="Conversion rate" value={`${d.conversion_rate}%`} tint="amber" testid="stat-conv-rate" />
            <StatCard icon={IndianRupee} label="Commission earned" value={inr(d.commission_total)} tint="green" testid="stat-commission" />
            <StatCard icon={IndianRupee} label="Sales value" value={inr(d.sale_total)} tint="slate" testid="stat-sales" />
          </div>

          <SectionCard title="Conversions by status" testid="status-breakdown">
            {Object.keys(d.status_breakdown || {}).length === 0 ? (
              <p className="text-sm text-slate-500">No conversions received yet. They'll appear here once Cuelinks posts back a sale.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {Object.entries(d.status_breakdown).map(([st, v]) => (
                  <div key={st} data-testid={`status-${st}`} className={`rounded-xl px-4 py-3 ${STATUS_TINT[st] || STATUS_TINT.unknown}`}>
                    <p className="text-xs font-bold uppercase tracking-wide">{st}</p>
                    <p className="mt-1 text-lg font-bold">{v.count}</p>
                    <p className="text-xs">{inr(v.commission)} commission</p>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <div className="grid gap-6 lg:grid-cols-2">
            <SectionCard title="Top merchants" testid="top-merchants">
              {(d.top_merchants || []).length === 0 ? (
                <p className="text-sm text-slate-500">No merchant conversions yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="pb-2">Merchant</th><th className="pb-2 text-right">Conversions</th><th className="pb-2 text-right">Commission</th></tr></thead>
                  <tbody>
                    {d.top_merchants.map((m) => (
                      <tr key={m.merchant} className="border-t border-slate-100" data-testid={`merchant-row-${m.merchant}`}>
                        <td className="py-2 font-medium text-slate-700"><Store className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{m.merchant}</td>
                        <td className="py-2 text-right">{m.count}</td>
                        <td className="py-2 text-right font-semibold text-emerald-600">{inr(m.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>

            <SectionCard title="Clicks by source" testid="clicks-by-source">
              {Object.keys(d.clicks_by_source || {}).length === 0 ? (
                <p className="text-sm text-slate-500">No partner clicks logged yet.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(d.clicks_by_source).map(([src, n]) => (
                    <div key={src} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`source-${src}`}>
                      <span className="font-medium capitalize text-slate-700">{src}</span>
                      <span className="font-bold text-slate-900">{n}</span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Recent conversions" testid="recent-conversions">
            {(d.recent || []).length === 0 ? (
              <p className="text-sm text-slate-500">No conversions yet. Once a shopper buys through a partner link and Cuelinks posts back, it shows here — matched to the user by their Sub-ID.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="pb-2">Shopper</th><th className="pb-2">Merchant</th><th className="pb-2 text-right">Sale</th><th className="pb-2 text-right">Commission</th><th className="pb-2">Status</th><th className="pb-2">When</th></tr></thead>
                  <tbody>
                    {d.recent.map((c) => (
                      <tr key={c.transaction_id} className="border-t border-slate-100" data-testid={`conv-row-${c.transaction_id}`}>
                        <td className="py-2 text-slate-700">{c.user}</td>
                        <td className="py-2 text-slate-600">{c.campaign || "—"}</td>
                        <td className="py-2 text-right">{inr(c.sale_amount)}</td>
                        <td className="py-2 text-right font-semibold text-emerald-600">{inr(c.commission)}</td>
                        <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_TINT[(c.status || "unknown").toLowerCase()] || STATUS_TINT.unknown}`}>{c.status || "—"}</span></td>
                        <td className="py-2 text-xs text-slate-400">{(c.received_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
